import { Gavel } from "lucide-react";
import { useEffect, useState, useMemo } from "react";

interface LoadingStep {
  label: string;
  completed: boolean;
}

interface SplashScreenProps {
  onComplete: () => void;
  loadingSteps: LoadingStep[];
  minDuration?: number;
}

interface Particle {
  id: number;
  x: number;
  y: number;
  size: number;
  duration: number;
  delay: number;
  opacity: number;
}

const FloatingParticles = () => {
  const particles = useMemo<Particle[]>(() => {
    return Array.from({ length: 25 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 8 + 4,
      duration: Math.random() * 8 + 6,
      delay: Math.random() * 5,
      opacity: Math.random() * 0.5 + 0.2,
    }));
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((particle) => (
        <div
          key={particle.id}
          className="absolute animate-float"
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            animationDuration: `${particle.duration}s`,
            animationDelay: `${particle.delay}s`,
          }}
        >
          <div
            className="rounded-full"
            style={{
              width: particle.size,
              height: particle.size,
              opacity: particle.opacity,
              background: `radial-gradient(circle, hsl(180 80% 80% / 0.8), hsl(265 70% 80% / 0.4))`,
            }}
          />
        </div>
      ))}
    </div>
  );
};

export const SplashScreen = ({ onComplete, loadingSteps, minDuration = 800 }: SplashScreenProps) => {
  const [isExiting, setIsExiting] = useState(false);
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);

  const completedSteps = loadingSteps.filter((step) => step.completed).length;
  const totalSteps = loadingSteps.length;
  const progress = totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;
  const allStepsComplete = completedSteps === totalSteps;

  const currentStep = loadingSteps.find((step) => !step.completed)?.label || "Ready!";

  useEffect(() => {
    const timer = setTimeout(() => {
      setMinTimeElapsed(true);
    }, minDuration);

    return () => clearTimeout(timer);
  }, [minDuration]);

  useEffect(() => {
    if (allStepsComplete && minTimeElapsed) {
      setIsExiting(true);
      setTimeout(onComplete, 500);
    }
  }, [allStepsComplete, minTimeElapsed, onComplete]);

  return (
    <div
      className={`fixed inset-0 z-50 flex flex-col items-center justify-center gradient-mesh transition-opacity duration-500 ${
        isExiting ? "opacity-0" : "opacity-100"
      }`}
      style={{
        background: 'linear-gradient(135deg, hsl(260 40% 25%), hsl(250 22% 12%), hsl(270 35% 20%))',
      }}
    >
      {/* Animated mesh overlay */}
      <div className="absolute inset-0 gradient-mesh opacity-60" />

      {/* Floating Particles */}
      <FloatingParticles />

      {/* Logo Container */}
      <div className={`flex flex-col items-center gap-6 z-10 ${isExiting ? "scale-110" : "animate-scale-in"}`}>
        {/* Gavel Icon with Radial Pulse */}
        <div className="relative radial-pulse">
          <div className="absolute inset-0 animate-ping opacity-20">
            <Gavel className="w-20 h-20 text-primary-foreground" />
          </div>
          <div className="w-24 h-24 rounded-full bg-primary-foreground/10 backdrop-blur-sm flex items-center justify-center border border-primary-foreground/20">
            <Gavel className="w-12 h-12 text-primary-foreground drop-shadow-lg" />
          </div>
        </div>

        {/* App Name with shimmer */}
        <div className="text-center">
          <h1 className="text-5xl font-extrabold text-primary-foreground tracking-tight drop-shadow-lg">
            <span className="text-shimmer">SMASH</span>{" "}
            <span className="font-light italic opacity-90">or</span>{" "}
            <span className="text-shimmer">PASS</span>
          </h1>
          <p className="mt-3 text-primary-foreground/70 text-lg font-light tracking-wide">Find Your Match</p>
        </div>
      </div>

      {/* Shimmer Progress Bar */}
      <div className="absolute bottom-20 w-56 z-10">
        <div className="h-1.5 bg-primary-foreground/20 rounded-full overflow-hidden backdrop-blur-sm">
          <div
            className="h-full rounded-full transition-all duration-300 ease-out relative"
            style={{
              width: `${progress}%`,
              background: 'linear-gradient(90deg, hsl(180 80% 80%), hsl(265 70% 80%), hsl(180 80% 80%))',
              backgroundSize: '200% 100%',
              animation: 'shimmer-text 2s ease infinite',
            }}
          />
        </div>
        <p className="mt-3 text-center text-primary-foreground/60 text-sm font-medium">{currentStep}</p>
      </div>
    </div>
  );
};
